import React, { useEffect, useRef, useState } from "react";

export default function InfiniteCarousel({
    items = [],
    renderItem,
    className = "",
    itemClassName = "",
    prevAriaLabel = "Previous item",
    nextAriaLabel = "Next item",
    arrowVariant = "wood"
}) {
    const [virtualCurrent, setVirtualCurrent] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    const touchStartX = useRef(null);
    const touchStartY = useRef(null);
    const touchLockRef = useRef(null);

    const next = () => {
        if (isAnimating || items.length <= 1) return;
        setIsAnimating(true);
        setVirtualCurrent((prev) => prev + 1);
    };

    const prev = () => {
        if (isAnimating || items.length <= 1) return;
        setIsAnimating(true);
        setVirtualCurrent((prev) => prev - 1);
    };

    useEffect(() => {
        if (!isAnimating) return;

        const timeout = setTimeout(() => {
            setIsAnimating(false);
        }, 350);

        return () => clearTimeout(timeout);
    }, [virtualCurrent, isAnimating]);

    const handleTouchStart = (event) => {
        const touch = event.touches[0];
        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
        touchLockRef.current = null;
    };

    const handleTouchMove = (event) => {
        if (touchStartX.current === null || touchStartY.current === null) return;

        const touch = event.touches[0];
        const deltaX = touch.clientX - touchStartX.current;
        const deltaY = touch.clientY - touchStartY.current;
        const axisThreshold = 8;

        if (touchLockRef.current === null) {
            if (Math.abs(deltaX) < axisThreshold && Math.abs(deltaY) < axisThreshold) return;
            touchLockRef.current = Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical";
        }

        if (touchLockRef.current === "horizontal") {
            event.preventDefault();
        }
    };

    const handleTouchEnd = (event) => {
        if (touchStartX.current === null || touchStartY.current === null) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - touchStartX.current;
        const deltaY = touch.clientY - touchStartY.current;

        const swipeThreshold = 60;
        const verticalTolerance = 25;

        const isHorizontalSwipe =
            Math.abs(deltaX) > swipeThreshold &&
            Math.abs(deltaX) > Math.abs(deltaY) + verticalTolerance;

        if (touchLockRef.current === "horizontal" && isHorizontalSwipe) {
            if (deltaX > 0) prev();
            else next();
        }

        touchStartX.current = null;
        touchStartY.current = null;
        touchLockRef.current = null;
    };

    if (!items.length) return null;

    return (
        <div className={`carousel-container carousel-container--${arrowVariant} ${className}`}>
            <button
                className="carousel-arrow carousel-arrow-prev"
                onClick={prev}
                disabled={isAnimating}
                aria-disabled={isAnimating}
                aria-label={prevAriaLabel}
            />

            <div className={`infinite-carousel`}>
                <div
                    className="carrousel"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    {(() => {
                        const windowSize = 2;
                        const visibleItems = [];

                        for (let i = virtualCurrent - windowSize; i <= virtualCurrent + windowSize; i++) {
                            visibleItems.push(i);
                        }

                        return visibleItems.map((vIndex) => {
                            const itemIndex = ((vIndex % items.length) + items.length) % items.length;
                            const item = items[itemIndex];
                            const offset = vIndex - virtualCurrent;

                            let positionClass = "";
                            if (offset === 0) positionClass = "center";
                            else if (offset === -1) positionClass = "left";
                            else if (offset === 1) positionClass = "right";
                            else if (offset < 0) positionClass = "hidden-left";
                            else positionClass = "hidden-right";

                            const prefixedPositionClass = itemClassName
                                ? `${itemClassName}-${positionClass}`
                                : positionClass;

                            return (
                                <div
                                    key={vIndex}
                                    className={`${itemClassName} ${prefixedPositionClass}`.trim()}
                                >
                                    {renderItem(item, itemIndex, vIndex, offset)}
                                </div>
                            );
                        });
                    })()}
                </div>
            </div>

            <button
                className="carousel-arrow carousel-arrow-next"
                onClick={next}
                disabled={isAnimating}
                aria-disabled={isAnimating}
                aria-label={nextAriaLabel}
            />
        </div>
    );
}